// Schema-driven statutory-identifier input (NIN, TIN, NSSF, passport, …). The
// value/onChange contract is the NORMALIZED value (digits-only for
// normalizedFormat="digits", trimmed text otherwise) so storage keeps one
// canonical form; the displayMask is applied only for on-screen presentation.
// Paste-tolerant (any separators the user pastes are stripped), leading zeros are
// preserved, and it never silently truncates below what the pack allows.
//
// Driven entirely by the pack field definition — no country hardcoded here.
import React, { useMemo } from 'react';
import type { IdentifierField } from '@/features/payroll/types/employeeProfileSchema';
import { applyMask, normalizeValue } from '@/features/payroll/types/employeeProfileSchema';

export const MaskedIdentifierInput: React.FC<{
  field: IdentifierField;
  value: string;                 // normalized value
  onChange: (normalized: string) => void;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}> = ({ field, value, onChange, id, disabled, invalid, describedBy }) => {
  const digits = field.normalizedFormat === 'digits';
  const display = useMemo(() => applyMask(field, value ?? ''), [field, value]);

  return (
    <input
      id={id}
      type="text"
      inputMode={digits ? 'numeric' : 'text'}
      autoComplete="off"
      autoCapitalize={digits ? 'off' : 'characters'}
      spellCheck={false}
      value={display}
      disabled={disabled}
      placeholder={field.placeholder}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(normalizeValue(field, e.target.value))}
      className={`w-full rounded-lg border bg-background text-foreground text-sm px-3 py-2 tabular-nums outline-none focus:border-orange-400 disabled:bg-muted disabled:text-muted-foreground ${
        invalid ? 'border-red-400' : 'border-border'
      }`}
    />
  );
};

export default MaskedIdentifierInput;
